import {
  Alert,
  AlertIcon,
  AlertDescription,
  TreeList,
  VStack,
  Button,
  Icon,
  Flex,
  useDisclosure,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AreYouSureModal from '../../../../../../../common/src/components/AreYouSureModal';
import { useCompanyScanDispatch } from '../../../../../../../common/src/utils/globalVariables';
import { TreeItem } from '../../../../../../../design-system/src/content/components/TreeList';
import { setQueuedScrollId } from '../../../../../../../company-scan/store/redux/slices/companyScanSlice';
import useFlowsMove from '../../../../../hooks/useFlowsMove';
import { useAskLaterQuestionsMutation } from './api/postAskLaterQuestion';
import { AskLaterQuestion } from './types';

interface Props {
  data: TreeItem[];
  isRemoveEnabled?: boolean;
  askLaterQuestions: AskLaterQuestion[];
  onClose?: () => void;
}

const AskLaterQuestionsModalBody: React.FC<Props> = ({
  data,
  isRemoveEnabled,
  askLaterQuestions,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    isOpen: isAreYouSureModalOpen,
    onOpen: onOpenAreYouSureModal,
    onClose: onCloseAreYouSureModal,
  } = useDisclosure();

  const companyScanDispatch = useCompanyScanDispatch();
  const { mutate } = useAskLaterQuestionsMutation();
  const { moveToQuestionPage } = useFlowsMove();

  const handleView = (dataItemKey: string) => {
    moveToQuestionPage(dataItemKey);
    onClose?.();
    companyScanDispatch(setQueuedScrollId(dataItemKey));
  };

  const handleRemove = (dataItemKey: string) => {
    mutate([
      {
        question_id: dataItemKey,
        active: false,
      },
    ]);
  };

  const handleRemoveAllButton = () => {
    onCloseAreYouSureModal();
    mutate(
      askLaterQuestions.map(({ question_id }) => ({
        question_id,
        active: false,
      })),
    );
  };

  return (
    <>
      <AreYouSureModal
        isOpen={isAreYouSureModalOpen}
        onClose={onCloseAreYouSureModal}
        onConfirm={handleRemoveAllButton}
        description={t('COMPANY-SCAN_ASK-LATER_MODAL_REMOVE-ALL')}
      />
      <VStack align="stretch" spacing="3">
        {isRemoveEnabled && (
          <>
            <Alert status="info" p="3">
              <AlertIcon />
              <AlertDescription>
                {t(
                  'COMPANY-SCAN_QUESTION-FLOW_ASKED-LATER-QUESTIONS_MODAL_ALERT',
                )}
              </AlertDescription>
            </Alert>
            <Flex justifyContent="flex-end">
              <Button
                size="sm"
                onClick={onOpenAreYouSureModal}
                variant="dangerGhost"
                leftIcon={<Icon name="GeneralActionsThrash" boxSize={5} />}
              >
                {t(
                  'COMPANY-SCAN_QUESTION-FLOW_ASKED-LATER-QUESTIONS_MODAL_REMOVE-ALL-QUESTIONS',
                )}
              </Button>
            </Flex>
          </>
        )}
        <TreeList
          onView={handleView}
          onRemove={handleRemove}
          data={data}
          noItemsMessage={t(
            'COMPANY-SCAN_QUESTION-FLOW_ASKED-LATER-QUESTIONS_MODAL_EMPTY',
          )}
          isRemoveEnabled={isRemoveEnabled}
          initiallyOpen
        />
      </VStack>
    </>
  );
};

export default AskLaterQuestionsModalBody;
